import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Rss } from "lucide-react";

export type MarketingKit = {
  instagram_post?: string;
  instagram_story?: string;
  facebook_post?: string;
  linkedin_post?: string;
  whatsapp_broadcast?: string;
  email_subject?: string;
  email_body?: string;
  flyer_headline?: string;
  flyer_subheadline?: string;
  flyer_bullets?: string[];
  hashtags?: string[];
  generated_at?: string | null;
};

export const EMPTY_PHASE6: { marketing_kit: MarketingKit; syndication_enabled: boolean } = {
  marketing_kit: {},
  syndication_enabled: false,
};

type Props = {
  value: { marketing_kit: MarketingKit; syndication_enabled: boolean };
  onChange: (v: { marketing_kit: MarketingKit; syndication_enabled: boolean }) => void;
  listingContext?: any;
};

export default function Phase6Form({ value, onChange, listingContext }: Props) {
  const v = value || EMPTY_PHASE6;
  const kit = v.marketing_kit || {};
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!listingContext?.title) {
      toast.error("Preencha ao menos o título do imóvel.");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-marketing-kit", {
        body: { listing: listingContext },
      });
      if (error) throw error;
      onChange({ ...v, marketing_kit: data as MarketingKit });
      toast.success("Kit de marketing gerado!");
    } catch (e: any) {
      toast.error(e?.message || "Falha ao gerar kit");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const field = (key: keyof MarketingKit, label: string, rows = 3) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold">{label}</Label>
        <Button type="button" size="sm" variant="ghost" onClick={() => copy(kit[key] as string)}>
          <Copy className="w-3 h-3 mr-1" /> Copiar
        </Button>
      </div>
      <Textarea
        rows={rows}
        value={(kit[key] as string) || ""}
        onChange={(e) => onChange({ ...v, marketing_kit: { ...kit, [key]: e.target.value } })}
      />
    </div>
  );

  return (
    <details className="border border-border rounded-md" open>
      <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
        📣 Fase 6 — Central de Marketing e Distribuição
      </summary>
      <div className="p-3 space-y-4">
        {/* Feed XML */}
        <div className="flex items-center justify-between bg-muted/40 p-3 rounded">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Rss className="w-4 h-4" /> Feed XML para portais
            </div>
            <p className="text-xs text-muted-foreground">
              Quando ligado, este imóvel aparece no feed público em <code>/functions/v1/listings-feed</code> para ZAP, VivaReal, OLX etc.
            </p>
          </div>
          <Switch
            checked={v.syndication_enabled}
            onCheckedChange={(checked) => onChange({ ...v, syndication_enabled: checked })}
          />
        </div>

        {/* AI Kit */}
        <div className="flex items-center justify-between gap-3 bg-muted/40 p-3 rounded">
          <div className="text-xs text-muted-foreground">
            Gere posts para Instagram, Facebook, LinkedIn, WhatsApp, e-mail e flyer impresso com IA.
          </div>
          <Button type="button" onClick={generate} disabled={loading} size="sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Gerar Kit
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {field("instagram_post", "📸 Instagram — Post", 5)}
          {field("instagram_story", "📖 Instagram — Story", 2)}
          {field("facebook_post", "👥 Facebook", 5)}
          {field("linkedin_post", "💼 LinkedIn", 5)}
          {field("whatsapp_broadcast", "💬 WhatsApp Broadcast", 4)}
          {field("email_subject", "📧 E-mail — Assunto", 2)}
        </div>

        {field("email_body", "📧 E-mail — Corpo (HTML)", 6)}

        <div className="grid md:grid-cols-2 gap-3">
          {field("flyer_headline", "🖨️ Flyer — Manchete", 2)}
          {field("flyer_subheadline", "🖨️ Flyer — Subtítulo", 2)}
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">🎯 Bullets do Flyer</Label>
            <Button type="button" size="sm" variant="ghost" onClick={() => copy((kit.flyer_bullets || []).map(b => `• ${b}`).join("\n"))}>
              <Copy className="w-3 h-3 mr-1" /> Copiar
            </Button>
          </div>
          <Textarea
            rows={5}
            value={(kit.flyer_bullets || []).join("\n")}
            onChange={(e) => onChange({ ...v, marketing_kit: { ...kit, flyer_bullets: e.target.value.split("\n").filter(Boolean) } })}
            placeholder="Um por linha"
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold"># Hashtags</Label>
            <Button type="button" size="sm" variant="ghost" onClick={() => copy((kit.hashtags || []).map(h => `#${h}`).join(" "))}>
              <Copy className="w-3 h-3 mr-1" /> Copiar
            </Button>
          </div>
          <Textarea
            rows={2}
            value={(kit.hashtags || []).join(", ")}
            onChange={(e) => onChange({ ...v, marketing_kit: { ...kit, hashtags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } })}
            placeholder="separadas por vírgula, sem o #"
          />
        </div>

        {kit.generated_at && (
          <p className="text-xs text-muted-foreground">
            Gerado em: {new Date(kit.generated_at).toLocaleString("pt-BR")}
          </p>
        )}
      </div>
    </details>
  );
}
